// The app manifest: the declarative surface an app publishes so the product
// frontend knows what it can render and call. One manifest per app, versioned
// by `manifestVersion` so an older host can reject a shape it cannot render.
//
// Parse-only by design: this module is the single source of truth for the
// manifest SHAPE (zod schemas + the inferred TypeScript types). It deliberately
// does NOT emit JSON Schema and does NOT reach the app store, blobs, or any
// route — a manifest is data that arrives from an app bundle, so callers parse
// it at the trust boundary and hand the parsed value inward.
//
// Loud-parse posture, matching src/apps/bundle.ts: unknown keys are rejected
// (`.strict()`), so a typo'd `slotIDs` fails at the boundary instead of
// silently disabling a widget at render time.
//
// A HAND-SYNCED MIRROR of lumi-mcp-server's src/app-types/manifest.ts, which is
// where an upload is accepted or refused. Every change to either copy belongs in
// both: see syncing-with-lumi-mcp-server.md beside this package.

import { z } from 'zod';

import { MODEL_CLASSES, SchemaModelClass } from './llm';

// Bumped only when the manifest shape changes incompatibly. A host reads this
// FIRST and refuses a version it does not understand; the current (and only)
// version is 1.
export const MANIFEST_VERSION = 1;

// Ids are opaque keys, referenced from routes, slots, and workflow dispatch, so
// they stay to a conservative URL-safe grammar: lowercase alnum segments joined
// by single dashes. Same rule as APP_NAME_RE (src/apps/constants.ts) — kept as
// its OWN constant rather than imported, because an app-internal widget id and
// a tenant-wide app name are unrelated namespaces that happen to agree today.
export const MANIFEST_ID_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const manifestId = z
    .string()
    .min(1)
    .max(64)
    .regex(MANIFEST_ID_RE, 'must be lowercase alphanumeric segments joined by single dashes');

// Params a host passes into a widget or workflow at invocation time. Declared
// as an opaque object body: we pin `type: 'object'` (every params schema is a
// keyed bag, never a bare scalar or array) and carry `properties` through
// UNVALIDATED. The frontend, not this module, owns the property-level dialect,
// and validating it here would force a lockstep bump every time the frontend
// grows a keyword.
export const paramsSchemaSchema = z
    .object({
        type: z.literal('object'),
        properties: z.record(z.string(), z.unknown()),
        required: z.array(z.string()).optional(),
    })
    .strict();

// What every table slot hands a button: the view the user is looking at, never
// its rows.
export const TABLE_VIEW_PARAMS = {
    columns: {
        type: 'object',
        describe:
            'The visible columns in order, each with the row fields behind it and, where it computes its value, how',
    },
    filters: { type: 'object', describe: 'The active filters, each naming a column in `columns`' },
    sort: { type: 'object', describe: 'The active sort, each entry naming a column in `columns`' },
    search: { type: 'object', describe: 'The free-text search and the columns it covers, when there is one' },
} as const satisfies Record<string, SlotParamDefinition>;

/**
 * One param a slot supplies. `type` decides where the value may go: only a
 * `string` param can be mapped into a URL by a `navigate` action, since an
 * object has no single spelling in a path segment.
 */
export interface SlotParamDefinition {
    type: 'string' | 'object';
    describe: string;
}

// What a table's page knows that its rows do not, by the name a table slot
// lists it under.
export const PAGE_PARAMS = {
    rfqId: { type: 'string', describe: 'The project on screen (the `:projectId` in its URL)' },
    assemblyId: { type: 'string', describe: 'The assembly on screen' },
    manufacturingAssemblyDetailsId: { type: 'string', describe: 'The manufacturing assembly details on screen' },
    customerId: { type: 'string', describe: 'The customer on screen' },
    ipnId: { type: 'string', describe: 'The IPN on screen' },
    partId: { type: 'string', describe: 'The part on screen' },
    customPartSpecificationId: { type: 'string', describe: 'The custom part on screen' },
    manufacturerId: { type: 'string', describe: 'The manufacturer on screen' },
} as const satisfies Record<string, SlotParamDefinition>;

export type PageParamName = keyof typeof PAGE_PARAMS;

/** A table an app may put buttons over: which one, the page it is on, and what that page adds to the view. */
export interface TableSlotDefinition {
    table: string;
    page: string;
    params?: readonly PageParamName[];
}

// Each entry becomes `slots.<name>.table.buttons`. Hand-synced with
// lumi-mcp-server's src/app-types.
export const TABLE_SLOTS = {
    projects: { table: 'projects', page: '/projects' },
    'project-progress-history': { table: 'progress history', page: '/projects' },
    'home-projects': { table: 'projects', page: '/home' },
    assemblies: { table: 'assemblies', page: '/assemblies' },
    drivers: { table: 'drivers', page: '/manufacturing/drivers' },
    'manufacturing-resources': { table: 'resources', page: '/manufacturing/resources' },
    customers: { table: 'customers', page: '/customers' },
    components: { table: 'components', page: '/parts/components' },
    parts: { table: 'off-the-shelf parts', page: '/parts/search' },
    'custom-parts': { table: 'custom parts', page: '/parts/search' },
    'part-alerts': { table: 'part alerts', page: '/parts/alerts' },
    manufacturers: { table: 'manufacturers', page: '/manufacturer' },
    'manufacturer-line-cards': { table: 'manufacturer line cards', page: '/manufacturer/line-card' },
    'risk-parts': { table: 'parts', page: '/risk-management/library-health/parts' },
    'risk-components': { table: 'components', page: '/risk-management/library-health/components' },
    'risk-assemblies': { table: 'assemblies', page: '/risk-management/library-health/assemblies' },
    'geo-risk-events': { table: 'geo risk events', page: '/risk-management/geo-risk' },
    pcns: { table: 'PCNs', page: '/risk-management/pcns' },
    'risk-digest-pcn-alerts': { table: 'PCN alerts', page: '/risk-management/risk-digest' },
    'risk-digest-assemblies': { table: 'most affected assemblies', page: '/risk-management/risk-digest' },
    'risk-digest-geo-risks': { table: 'geo risks', page: '/risk-management/risk-digest' },
    users: { table: 'users', page: '/settings/organization/users' },
    'company-customer-users': { table: 'users', page: '/settings/company' },
    'customer-specific-settings': {
        table: 'customer-specific settings',
        page: '/settings/organization/customer-portal/customer-specific-settings',
    },
    'machine-tokens': { table: 'machine tokens', page: '/settings/organization/api-integration' },
    'csv-integrations': { table: 'CSV integrations', page: '/settings/organization/csv-integration' },
    'custom-part-categories': {
        table: 'custom part categories',
        page: '/settings/organization/custom-part-categories',
    },
    'feature-flags': { table: 'feature flags', page: '/settings/organization/feature-flags' },
    'manufacturer-preferences': {
        table: 'manufacturer preferences',
        page: '/settings/organization/manufacturer-preferences',
    },
    'pcb-panel-preferences': { table: 'PCB panel preferences', page: '/settings/organization/pcb' },
    'action-permissions': {
        table: 'action permissions',
        page: '/settings/organization/user-management/action-permissions',
    },
    'module-role-permissions': {
        table: 'module permissions by role',
        page: '/settings/organization/user-management/action-permissions',
    },
    roles: { table: 'roles', page: '/settings/organization/user-management/roles' },
    sites: { table: 'sites', page: '/settings/organization/sites' },
    'supplier-api-integrations': {
        table: 'supplier API integrations',
        page: '/settings/organization/supplier-api-integrations',
    },
    'quotation-templates': { table: 'quotation templates', page: '/templates/quotation' },
    'pcb-specification-templates': { table: 'PCB specification templates', page: '/templates/pcb-specification' },
    'calculation-templates': { table: 'calculation templates', page: '/templates/calculation' },
    'flexible-calculation-templates': {
        table: 'flexible calculation templates',
        page: '/template/flexible-calculation',
    },
    'exported-variables': { table: 'exported variables', page: '/template/flexible-calculation/exported-variables' },

    'flexible-calculation-saved': {
        table: 'saved calculations',
        page: '/projects/:projectId/flexible-calculation',
        params: ['rfqId'],
    },
    'bom-assemblies': { table: 'assemblies', page: '/projects/:projectId/bom', params: ['rfqId'] },
    bom: { table: 'BOM', page: '/projects/:projectId/bom/assembly/:assemblyId', params: ['rfqId', 'assemblyId'] },
    'bom-subassemblies': {
        table: 'subassemblies',
        page: '/projects/:projectId/bom/assembly/:assemblyId/details',
        params: ['rfqId', 'assemblyId'],
    },
    'assembly-drivers': {
        table: 'drivers',
        page: '/projects/:projectId/manufacturing/:manufacturingAssemblyDetailsId',
        params: ['rfqId', 'manufacturingAssemblyDetailsId'],
    },

    'assembly-bom': { table: 'BOM', page: '/assemblies/:assemblyId/dashboard', params: ['assemblyId'] },
    'assembly-inventory': { table: 'inventory', page: '/assemblies/:assemblyId/dashboard', params: ['assemblyId'] },
    'assembly-subassemblies': {
        table: 'subassemblies',
        page: '/assemblies/:assemblyId/details',
        params: ['assemblyId'],
    },
    'customer-users': { table: 'users', page: '/customers/:customerId', params: ['customerId'] },
    'ipn-cpns': { table: 'CPNs', page: '/parts/components/ipn/cpns', params: ['ipnId'] },
    'ipn-used-in': { table: 'used in', page: '/parts/components/ipn/used-in', params: ['ipnId'] },
    'part-alternatives': {
        table: 'part alternatives',
        page: '/parts/details/:partId/part-alternatives',
        params: ['partId'],
    },
    'part-usage': { table: 'used in', page: '/parts/details/:partId/used-in', params: ['partId'] },
    'custom-part-usage': {
        table: 'used in',
        page: '/parts/custom/details/:customPartSpecificationId/used-in',
        params: ['customPartSpecificationId'],
    },
    'manufacturer-line-card': {
        table: 'line card',
        page: '/manufacturer/:manufacturerId',
        params: ['manufacturerId'],
    },
} as const satisfies Record<string, TableSlotDefinition>;

export type TableSlotName = keyof typeof TABLE_SLOTS;
export type TableSlotId = `slots.${TableSlotName}.table.buttons`;

export const tableSlotId = (name: TableSlotName): TableSlotId => `slots.${name}.table.buttons`;

/**
 * A place in the product an installed app may put something.
 *
 * `params` is what the host supplies there, by name — the contract a widget's
 * `paramsSchema` is checked against at upsert, and the source the app-facing
 * docs are written from. A full-page widget declares its own route, so it brings
 * its own params and the slot names none.
 */
export interface SlotDefinition {
    kind: (typeof WIDGET_KINDS)[number];
    where: string;
    params?: Readonly<Record<string, SlotParamDefinition>>;
}

function tableSlot({ table, page, params = [] }: TableSlotDefinition): SlotDefinition {
    return {
        kind: 'button',
        where: `The ${table} table's menu bar on ${page}, inside its Apps menu`,
        params: {
            ...TABLE_VIEW_PARAMS,
            ...Object.fromEntries(params.map((name) => [name, PAGE_PARAMS[name]])),
        },
    };
}

const TABLE_SLOT_DEFINITIONS = Object.fromEntries(
    Object.entries(TABLE_SLOTS).map(([name, slot]) => [tableSlotId(name as TableSlotName), tableSlot(slot)]),
) as Record<TableSlotId, SlotDefinition>;

// Closed set: a host can only render slots it has code for, so an unknown slot
// id is an authoring error worth failing at upsert rather than a silently dead
// widget. Hand-synced with lumi-mcp-server's src/app-types.
export const SLOTS: Readonly<
    Record<
        | 'slots.full-page'
        | 'slots.bom.import.buttons'
        | 'slots.flexible-calculation.buttons'
        | 'slots.flexible-calculation.summary.buttons'
        | 'slots.flexible-calculation.calculation.menu'
        | 'slots.customer.details.container'
        | 'slots.settings.csv-import.container'
        | 'slots.manufacturing.templates.tabs'
        | 'slots.flexible-calculation.templates.tabs'
        | TableSlotId,
        SlotDefinition
    >
> = {
    'slots.full-page': {
        kind: 'full-page',
        where: "Its own route in the product's router",
    },
    'slots.bom.import.buttons': {
        kind: 'button',
        where: 'The assembly BOM toolbar, next to Import BOM',
        params: {
            projectId: { type: 'string', describe: 'The id of the project whose BOM is open' },
            assemblyId: { type: 'string', describe: 'The id of the assembly whose BOM is open' },
        },
    },
    'slots.flexible-calculation.buttons': {
        kind: 'button',
        where: 'The flexible calculation toolbar, in its Apps menu, while a saved calculation is open and not being edited',
        params: {
            projectId: { type: 'string', describe: 'The id of the project the calculation belongs to' },
            flexibleCalculationId: { type: 'string', describe: 'The id of the flexible calculation that is open' },
        },
    },
    'slots.flexible-calculation.summary.buttons': {
        kind: 'button',
        where: 'The flexible calculation toolbar, in its Apps menu, while a summary is open',
        params: {
            projectId: { type: 'string', describe: 'The id of the project the summary belongs to' },
            summaryId: { type: 'string', describe: 'The id of the summary that is open' },
        },
    },
    'slots.flexible-calculation.calculation.menu': {
        kind: 'button',
        where:
            'The flexible calculation page’s Calculation menu ("..."), at the bottom after Delete calculation, ' +
            'while a saved calculation is open and not being edited',
        params: {
            projectId: { type: 'string', describe: 'The id of the project the calculation belongs to' },
            flexibleCalculationId: { type: 'string', describe: 'The id of the flexible calculation that is open' },
        },
    },
    'slots.customer.details.container': {
        kind: 'container',
        where: 'The customer details sidebar, below Customer-specific settings, about 360 px wide',
        params: {
            customerId: { type: 'string', describe: 'The id of the customer whose details page is open' },
        },
    },
    'slots.settings.csv-import.container': {
        kind: 'container',
        where: 'The CSV import settings page, above Files, at least 720 px wide',
        // The page is about the whole tenant, which the widget already knows.
        params: {},
    },
    'slots.manufacturing.templates.tabs': {
        kind: 'tab',
        where: 'The Manufacturing templates tab bar, after Templates, full width',
        // The page is about the whole tenant, which the widget already knows.
        params: {},
    },
    'slots.flexible-calculation.templates.tabs': {
        kind: 'tab',
        where: 'The Flexible calculation templates tab bar, after Exported variables, full width',
        // The page lists the whole tenant's templates, which the widget already knows.
        params: {},
    },
    ...TABLE_SLOT_DEFINITIONS,
};

export type SlotId = keyof typeof SLOTS;
export const SLOT_IDS = Object.keys(SLOTS) as [SlotId, ...SlotId[]];
export const slotIdSchema = z.enum(SLOT_IDS);

/** The params a slot supplies, by name. Empty when the widget brings its own. */
export function slotParamNames(slotId: SlotId): readonly string[] {
    return Object.keys(SLOTS[slotId].params ?? {});
}

/** The type of one param a slot supplies, or undefined when the slot does not supply it. */
export function slotParamType(slotId: SlotId, name: string): SlotParamDefinition['type'] | undefined {
    const params = (SLOTS[slotId] as SlotDefinition).params ?? {};
    return Object.hasOwn(params, name) ? params[name]!.type : undefined;
}

/**
 * Something a button's workflow or worker.js handler can ask the product to do,
 * keyed by where it puts it: in the run result for a workflow, at the top level
 * of the answer for a handler.
 *
 * Each effect owns its own response shape, so an app author reads one entry to
 * know what to return and the product reads the same entry to know what to act
 * on. `packages/frontend/slots` holds the handler for each key; a test pins the two sets
 * equal so an effect can never be declared without something doing it.
 */
export interface EffectDefinition {
    shape: string;
    describe: string;
}

export const EFFECTS = {
    download: {
        shape: '{ url: string }',
        describe:
            'Hands the user a file. From a workflow, mint the URL with `api.files.downloadUrl(path)`; from a ' +
            'worker.js handler, POST the bytes to `/apps/_api/apps/<app-id>/downloads?name=<file name>`. ' +
            'Return what either gives you as `download`. The URL must be `https:`.',
    },
    navigate: {
        shape: '{ path: string, replace?: boolean }',
        describe:
            'Sends the user to a page in the product, such as `/apps/<app-id>/<id>`. `path` starts with a single ' +
            '`/` and has no scheme, host, backslash or whitespace; a query and a fragment are fine. ' +
            '`replace: true` replaces the current history entry. A result with both `download` and `navigate` ' +
            'does neither.',
    },
} as const satisfies Record<string, EffectDefinition>;

export type EffectKey = keyof typeof EFFECTS;
export const EFFECT_KEYS = Object.keys(EFFECTS) as [EffectKey, ...EffectKey[]];

/**
 * The names a widget requires that its slot does not supply.
 *
 * The one rule behind both the upsert refusal and the host withholding a widget
 * at render, so an app cannot pass one and fail the other.
 */
export function unmetParamNames(slotId: SlotId, paramsSchema: ParamsSchema): string[] {
    // Only a full-page slot names no params at all: its widget brings them from its own route.
    if (SLOTS[slotId].params === undefined) return [];
    const supplied = new Set(slotParamNames(slotId));
    return (paramsSchema.required ?? []).filter((name) => !supplied.has(name));
}

// Widget kinds, the discriminant. `full-page` owns a route in the product's
// router and is reached by navigation; `button` is a control the host draws in
// a slot it already renders, has no route of its own, and does what its
// `action` says when clicked; `container` is a frame the host embeds inline in
// a page it already renders, with its height following the widget's content.
// A container slot shows one app: the host draws the usable widget with the
// lowest `app:widget` key and reports the rest, a rule no single upload can
// check. `tab` is a frame the host shows as one more tab in a tab bar it already
// renders; a tab slot shows every usable widget, each as its own tab.
export const WIDGET_KINDS = ['full-page', 'button', 'container', 'tab'] as const;

// The kinds that load a widget document in a frame: the upload requires
// `widgets/<id>.html` for them and the assignments route gives them a
// `bundle_url`. Every check that means "framed" reads this list, never a
// comparison with one kind.
export const FRAMED_WIDGET_KINDS = ['full-page', 'container', 'tab'] as const satisfies readonly WidgetKind[];
export type FramedWidgetKind = (typeof FRAMED_WIDGET_KINDS)[number];

// The kinds the host draws itself. They have no document.
const HOST_DRAWN_WIDGET_KINDS = ['button'] as const satisfies readonly WidgetKind[];

const isWidgetKindIn = <K extends WidgetKind>(kinds: readonly K[], kind: WidgetKind): kind is K =>
    (kinds as readonly WidgetKind[]).includes(kind);

export function isFramedWidgetKind(kind: WidgetKind): kind is FramedWidgetKind {
    if (isWidgetKindIn(FRAMED_WIDGET_KINDS, kind)) return true;
    if (isWidgetKindIn(HOST_DRAWN_WIDGET_KINDS, kind)) return false;
    // A kind in WIDGET_KINDS that is in neither list fails the typecheck here,
    // so a new kind cannot ship unclassified.
    return kind satisfies never;
}

// A route is a product-frontend path pattern under the `/apps` prefix:
// lowercase-dash segments, with `:param` placeholders whose names are
// lowercase-dash too (e.g. `/apps/contacts/:contact-id`). A param's name is the
// key its value arrives under. No query, no fragment, no trailing slash — the
// host owns those.
//
// The prefix is required so the product router has ONE static boundary for every
// app. FullPageSlots matches whatever pathname a manifest claims, so without it
// an uploaded app can take any top-level product route. Bare `/apps` is allowed,
// as the index an app directory lands on; bare `/apps/*` is not, since one app
// claiming the whole namespace would shadow every other.
//
// Hand-synced with lumi-mcp-server's src/app-types/manifest.ts, which enforces
// the same rule at upload.
//
// What it EXCLUDES carries as much weight as what it allows. `*`, `(`, `)`, `?`
// and `+` are all rejected, so that every route segment consumes exactly one path
// segment — the property the collision contract on `route` below is built on.
// A trailing `/*` is the one exception, letting the host router keep the same
// widget mounted for nested paths while leaving route matching and history in the
// host. Optional/repeated/custom-pattern segments stay excluded so specificity
// ordering remains deterministic and understandable.
export const WIDGET_ROUTE_RE = /^\/apps(?:(?:\/(?::?[a-z0-9]+(?:-[a-z0-9]+)*))+(?:\/\*)?)?$/;

const WIDGET_NAME_MAX_LENGTH = 120;
export const WIDGET_LOCALE_RE = /^[a-z]{2,3}(?:-(?:[A-Z][a-z]{3}|[A-Z]{2}|[0-9]{3}|[a-z0-9]{4,8}))*$/;
const FORBIDDEN_WIDGET_NAME_CHARACTER_RE = /[\p{Cc}\p{Zl}\p{Zp}]/u;
const VISIBLE_WIDGET_NAME_CHARACTER_RE = /[\p{L}\p{N}\p{S}]/u;

const SchemaVisibleWidgetName = z
    .string()
    .min(1)
    .refine(
        (name) => Array.from(name).length <= WIDGET_NAME_MAX_LENGTH,
        `must contain at most ${WIDGET_NAME_MAX_LENGTH} Unicode code points`,
    )
    .refine((name) => name.trim() === name, 'must not have leading or trailing whitespace')
    .refine(
        (name) => !FORBIDDEN_WIDGET_NAME_CHARACTER_RE.test(name),
        'must not contain control, line separator, or paragraph separator characters',
    )
    .refine((name) => VISIBLE_WIDGET_NAME_CHARACTER_RE.test(name), 'must contain a Unicode letter, number, or symbol');

const SchemaWidgetLocale = z
    .string()
    .max(35)
    .regex(
        WIDGET_LOCALE_RE,
        'must be a plausible BCP-47 locale tag with conventional casing (for example en or pt-BR)',
    );

const SchemaPlainObject = z.custom<Record<string, unknown>>(
    (value) =>
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.getPrototypeOf(value) === Object.prototype,
    'must be a plain object',
);

const SchemaLocalizedWidgetName = SchemaPlainObject.pipe(
    z
        .record(SchemaWidgetLocale, SchemaVisibleWidgetName)
        .refine((names) => Object.keys(names).length <= 20, 'must contain at most 20 locales')
        .refine((names) => Object.prototype.hasOwnProperty.call(names, 'en'), 'must contain an own en locale'),
);

/** A widget name can retain the legacy string form or provide localized labels. */
export const SchemaWidgetName = z.union([SchemaVisibleWidgetName, SchemaLocalizedWidgetName]);

export type WidgetName = z.infer<typeof SchemaWidgetName>;

/** Resolves an authored widget name without mutating it or consulting ambient locale state. */
export function resolveLocalizedName(name: WidgetName, locale: string): string {
    if (typeof name === 'string') return name;

    const entries = Object.entries(name);
    const find = (candidate: string): string | undefined =>
        entries.find(([key]) => key.toLowerCase() === candidate.toLowerCase())?.[1];
    const exact = find(locale);
    if (exact !== undefined) return exact;

    const primaryLanguage = locale.split('-')[0];
    return find(primaryLanguage) ?? find('en') ?? name.en;
}

const widgetBase = {
    id: manifestId,
    name: SchemaWidgetName,
    slotIds: z.array(slotIdSchema).min(1),
    paramsSchema: paramsSchemaSchema,
};

export const fullPageWidgetSchema = z
    .object({
        ...widgetBase,
        kind: z.literal('full-page'),
        // Required: a full-page widget IS a route, so a missing one leaves it
        // unreachable.
        //
        // A route is not exclusive, though: nothing stops two apps installed in the
        // same tenant from claiming URLs that overlap. That is a misconfiguration,
        // and the contract is that the host RESOLVES it rather than failing — the
        // customer keeps a working page, and the host reports the collision to its
        // error monitoring so somebody finds out. Resolution is deterministic and
        // deliberately independent of the order assignments arrive in, since the
        // slot-assignment API promises no ordering:
        //
        //   1. the more specific route wins — more static segments than `:param`
        //      segments; then, at equal counts, the route whose leftmost segment
        //      commits to a literal.
        //      A trailing wildcard is less specific than a `:param`, and with
        //      wildcards a route with more concrete segments wins.
        //   2. a remaining tie goes to the lower `${app.id}:${widget.id}`, compared
        //      lexicographically. Parameter-name length never participates.
        //   3. the losing widget is not rendered anywhere for that URL.
        //
        // Params are validated for the WINNER only: whether a widget's params are
        // usable must not decide which widget owns the URL, or a broken params
        // schema would silently hand a route to somebody else.
        route: z
            .string()
            .min(1)
            .max(200)
            .regex(
                WIDGET_ROUTE_RE,
                'must start with /apps/ and continue in lowercase-dash or :param segments, optionally ending in /*',
            ),
    })
    .strict();

/** The `:param` names in a full-page route, in order. A trailing `/*` names none. */
export function routeParamNames(route: string): string[] {
    return route
        .split('/')
        .filter((segment) => segment.startsWith(':'))
        .map((segment) => segment.slice(1));
}

// Where a mapped destination param reads its value from: a param the button's
// slot supplies, by name. Explicit on purpose — nothing is matched by name, so a
// slot growing a param never silently changes where a button goes.
const paramSourceSchema = z.object({ slot: z.string().min(1) }).strict();

// A worker.js handler a `call` button names. Wider than MANIFEST_ID_RE because
// handlers in use are camelCase (`getAssemblies`). Every character is unreserved
// in a URL path, so a valid name reads the same in the URL, and with no `.` it
// can never be `.` or `..`; `callUrl` still encodes it, so a name that bypassed
// this rule cannot reach another path. It binds only what a manifest may name: the
// call route itself takes any path segment, since widgets call it directly.
export const HANDLER_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

// What a clicked button does, from lumi-mcp-server's
// docs/superpowers/specs/2026-09-29-app-slot-kinds-and-actions-design.md:
// `workflow` runs one of the app's workflows, `navigate` goes to the app's own
// full-page widget, and `call` runs one of its worker.js handlers (Phase 2). The
// references `workflow` and `navigate` make are checked against the rest of the
// manifest in appManifestSchema's superRefine; a handler is not declared, so a
// missing one is a 404 at click time.
export const buttonActionSchema = z.discriminatedUnion('type', [
    z
        .object({
            type: z.literal('workflow'),
            // A workflow in the same manifest, by id. Its result may carry
            // EFFECTS, which the host carries out once the run answers.
            workflowId: manifestId,
        })
        .strict(),
    z
        .object({
            type: z.literal('navigate'),
            // The destination: a full-page widget in the same manifest, by id.
            to: z.object({ widget: manifestId }).strict(),
            // Destination param name → the slot param that supplies it. Keys are
            // the destination's route params and paramsSchema properties.
            params: z.record(z.string(), paramSourceSchema).default({}),
        })
        .strict(),
    z
        .object({
            type: z.literal('call'),
            // A handler in the app's worker.js. Its answer may carry EFFECTS at
            // the top level, which the host carries out once the call answers.
            handler: z
                .string()
                .regex(HANDLER_NAME_RE, 'must be 1–64 letters, digits, _ or -, starting with a letter or digit'),
        })
        .strict(),
]);

export const buttonWidgetSchema = z
    .object({
        ...widgetBase,
        kind: z.literal('button'),
        action: buttonActionSchema,
    })
    .strict();

// An inline frame on a page the host already renders. Nothing beyond the base:
// the slot supplies the params, the host sizes the frame, and `name` is its
// accessible title.
export const containerWidgetSchema = z
    .object({
        ...widgetBase,
        kind: z.literal('container'),
    })
    .strict();

// A tab in a tab bar the host already renders. The slot supplies the params, the
// host sizes the frame, and `name` is the tab's label. `order` sorts the app tabs
// of a slot, smallest first, with no `order` after every tab that has one; it
// never moves an app tab in front of the product's own tabs.
export const tabWidgetSchema = z
    .object({
        ...widgetBase,
        kind: z.literal('tab'),
        order: z.number().int().min(0).max(999).optional(),
    })
    .strict();

// Discriminated union on `kind` — so `route` is required exactly where it means
// something, and zod reports the failure against the right member instead of
// unioning every member's error set.
export const widgetSchema = z.discriminatedUnion('kind', [
    fullPageWidgetSchema,
    buttonWidgetSchema,
    containerWidgetSchema,
    tabWidgetSchema,
]);

// Optional author-provided discovery metadata. This remains declarative: asset
// paths name files the author uploads with the app, but this parse-only contract
// neither checks that those files exist nor defines how a host renders them.
// Featured status, usage/activity metrics, stars, and ranking are authoritative
// platform data and deliberately do not belong here; attribution is the one
// exception, and it is the top-level `author` field.
// Categories and tags are open vocabulary so authors can describe emerging
// use cases without a manifest-schema release (the fixed product area is the
// top-level `module`). Both arrive normalized rather
// than being silently transformed: lowercase alphanumeric words separated by
// exactly one space or hyphen, for example `supply chain` or
// `risk-compliance`.
export const CATALOG_TERM_RE = /^[a-z0-9]+(?:[ -][a-z0-9]+)*$/;
export const catalogCategorySchema = z
    .string()
    .min(1)
    .max(40)
    .regex(CATALOG_TERM_RE, 'must be lowercase alphanumeric words separated by a single space or hyphen');
export const catalogTagSchema = z
    .string()
    .min(1)
    .max(32)
    .regex(CATALOG_TERM_RE, 'must be lowercase alphanumeric words separated by a single space or hyphen');

export const catalogTagsSchema = z
    .array(catalogTagSchema)
    // Preserve author order while making repeated tags harmless. The limit
    // applies to the normalized unique output, not redundant input entries.
    .transform((tags) => [...new Set(tags)])
    .refine((tags) => tags.length <= 10, 'must contain at most 10 unique catalog tags');

// Catalog assets must be flat, canonical lowercase/hyphenated files in their
// reserved directory. Separate regexes keep each field's closed extension set
// visible to consumers of this contract. A declared path resolves against the
// per-app `assets_base_url` the slot assignments response carries; this host
// never assembles an apps-origin URL itself.
export const CATALOG_ICON_PATH_RE = /^assets\/catalog\/[a-z0-9]+(?:-[a-z0-9]+)*\.(?:svg|png|webp)$/;
export const CATALOG_COVER_IMAGE_PATH_RE = /^assets\/catalog\/[a-z0-9]+(?:-[a-z0-9]+)*\.(?:png|jpg|jpeg|webp|svg)$/;

export const catalogIconSchema = z
    .object({
        path: z.string().regex(CATALOG_ICON_PATH_RE, 'must be a canonical icon file under assets/catalog/'),
        alt: z.string().min(1).max(160),
    })
    .strict();

export const catalogCoverImageSchema = z
    .object({
        path: z
            .string()
            .regex(CATALOG_COVER_IMAGE_PATH_RE, 'must be a canonical cover image file under assets/catalog/'),
        alt: z.string().min(1).max(160),
    })
    .strict();

export const appCatalogSchema = z
    .object({
        summary: z.string().min(1).max(160).optional(),
        category: catalogCategorySchema.optional(),
        tags: catalogTagsSchema.optional(),
        icon: catalogIconSchema.optional(),
        coverImage: catalogCoverImageSchema.optional(),
    })
    .strict();

// ---------------------------------------------------------------------------
// Permissions: what the app's worker.js may call, declared for the app store
// and ENFORCED on the Workers-for-Platforms path. Every worker fetch funnels
// through the WfP outbound worker, which receives these declarations as
// dispatch params and denies anything undeclared — the app cannot forge its
// identity there, so this fence is sound. `permissions` is REQUIRED: an upload
// without it is refused, an explicit `{}` says the app calls nothing, and a
// manifest stored before the field became required reaches nothing until it
// declares and re-uploads.
//
// Browser-side calls are DISCLOSED by the same declaration but not soundly
// enforceable: a tenant's apps share an origin (standalone apps on the product
// host, widgets on the apps host), so client-supplied app identity is forgeable
// by a sibling app. The review gate and tenant self-authoring remain the
// controls there.
//
// `endpoints` grammar — EXACT ROUTES ONLY: `METHOD /path`, method ∈
// GET|POST|PUT|PATCH|DELETE (a worker's HEAD is covered by the matching GET
// pattern; OPTIONS has no first-party use and stays undeclarable), path under
// `/api` (the product API) or `/apps/_api` (the apps platform API: warehouse,
// scripts, storage, reports) with at least one segment after the prefix.
// There is NO subtree wildcard — a pattern names exactly ONE route, so the
// app-store disclosure is the literal endpoint list rather than a family a
// reader must expand. `*` is not a route wildcard either: it is the route
// template's PARAMETER marker (`GET /api/rfqs/*/status-history` ≡ the
// backend's `GET /rfqs/:rfqId/status-history`) — ids are runtime data, so a
// parameterized route is undeclarable without it. The bounds below exist
// because the flattened pattern list rides an HTTP header through the
// dispatcher, and Cloudflare caps a single header at 16KB — better a clear 400
// at upload than every call failing at the edge with an opaque error.
//
// An absent leg grants nothing on that surface, so `permissions: {}` is a
// total lockdown. The legs beyond `endpoints`:
//   `publicEndpoints` — endpoint patterns on the PUBLIC API
//                 (https://api.luminovo.com), in its own keyspace with no /api
//                 prefix added: `POST /offers/bulk`. `endpoints` names paths on
//                 mcp.luminovo.com, which serves the INTERNAL API; the two can
//                 share a path and answer it with different shapes, so each leg
//                 names one host and a declaration says which one it means.
//   `external`  — connections to external HTTPS hosts, reviewed by staff per
//                 (host, auth, scope, token host) and credentialed per app.
//   `srm`       — endpoint patterns in SRM's own `METHOD /path` keyspace.
//   `gateway`   — EXACT procedure names; no wildcard form.
//   `reports`   — the api.reports verbs a workflow may call.
//   `files`     — api.files.downloadUrl, minting a link to a file the workflow
//                 wrote in its own session.
//   `warehouse` — tables read via POST /apps/_api/warehouse/query. DISCLOSURE
//                 ONLY today: nothing enforces it, because that route cannot
//                 attribute an app on the shared browser origin.
//   `tenants`   — the Sibling Tenants an app's workflows may act in on the
//                 running person's behalf (its Reach), each one approved by
//                 staff per app. The same `endpoints` and `publicEndpoints`
//                 lists apply in every one of them.
//   `llm`       — the Model Classes the app's Completions and Decisions may name.
// The authoring namespaces (api.scripts/apps/egress/secrets) are not
// declarable at all.
export const PERMISSION_ENDPOINT_RE = /^(?:GET|POST|PUT|PATCH|DELETE) \/(?:api|apps\/_api)(?:\/(?:[a-z0-9._-]+|\*))+$/;
const PERMISSION_LLM_ROUTE_RE = /^[A-Z]+ \/apps\/_api\/apps\/[^/]+\/llm(?:\/|$)/;
// Hard ceiling on the TOTAL number of patterns across all grants — the
// header-size bound stated above, enforced in the superRefine below.
export const PERMISSION_MAX_TOTAL_PATTERNS = 100;
// The SRM twin, deliberately its OWN constant at the same value: SRM patterns
// never ride the x-api-patterns header (they filter the SRM endpoint set
// in-process), so they are NOT bound by the 16KB header cap that calibrates
// the number above. Sharing one constant would let an SRM-driven bump silently
// erode that margin.
export const PERMISSION_MAX_TOTAL_SRM_PATTERNS = 100;
// The public-API twin. These patterns DO ride a header of their own
// (x-public-endpoint-patterns), so the same 16KB reasoning as `endpoints`
// applies, per header; a separate constant keeps the two lists' caps
// independent.
export const PERMISSION_MAX_TOTAL_PUBLIC_ENDPOINT_PATTERNS = 100;
// Exact FQDN, matching the connection validator's write-time strictness in
// shape (lowercase labels, at least two).
export const PERMISSION_EXTERNAL_HOST_RE =
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

const permissionReason = z.string().min(1).max(200);

// One grant: a set of endpoint patterns plus the reason the store shows.
// Group related endpoints under one reason ("lists projects and BOMs") rather
// than one row per endpoint.
export const permissionEndpointGrantSchema = z
    .object({
        patterns: z
            .array(
                z
                    .string()
                    .min(1)
                    .max(120)
                    .regex(
                        PERMISSION_ENDPOINT_RE,
                        'must be "METHOD /api/..." or "METHOD /apps/_api/..." naming ONE route, with * for id segments (no ** — declare each endpoint)',
                    ),
            )
            .min(1)
            .max(16),
        reason: permissionReason.optional(),
    })
    .strict();

// A connection id: [a-z0-9.-], so a legacy entry may default it to its host.
export const CONNECTION_ID_RE = /^[a-z0-9](?:[a-z0-9.-]{0,251}[a-z0-9])?$/;
export const CONNECTION_AUTH_TYPES = ['none', 'static', 'oauth2_client_credentials'] as const;
export const CONNECTION_SCOPES = ['tenant', 'user'] as const;

const connectionHeaderSchema = z
    .object({ name: z.string().min(1).max(64).optional(), prefix: z.string().max(64).optional() })
    .strict();

// One external connection. Shape only: the https, first-party and header-name
// rules that need more than a regex run at upload, in the platform.
export const permissionExternalSchema = z
    .object({
        id: z.string().regex(CONNECTION_ID_RE, 'must be lowercase letters, digits, dots and hyphens').optional(),
        host: z.string().min(1).max(253).regex(PERMISSION_EXTERNAL_HOST_RE, 'must be an exact lowercase FQDN'),
        auth: z.enum(CONNECTION_AUTH_TYPES).default('none'),
        scope: z.enum(CONNECTION_SCOPES).default('tenant'),
        header: connectionHeaderSchema.optional(),
        tokenUrl: z.string().min(1).max(2048).optional(),
        scopes: z.string().max(500).optional(),
        audience: z.string().max(500).optional(),
        reason: permissionReason.optional(),
    })
    .strict()
    .superRefine((c, ctx) => {
        const credentialFields = [c.header, c.tokenUrl, c.scopes, c.audience].some((v) => v !== undefined);
        if (c.auth === 'none' && (credentialFields || c.scope === 'user')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'auth none takes no header, tokenUrl, scopes or audience and cannot be scope user',
            });
        }
        if (c.auth === 'oauth2_client_credentials' && c.tokenUrl === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tokenUrl'],
                message: 'oauth2_client_credentials requires tokenUrl',
            });
        }
        if (c.auth === 'static' && (c.tokenUrl !== undefined || c.scopes !== undefined || c.audience !== undefined)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'tokenUrl, scopes and audience are only valid for oauth2_client_credentials',
            });
        }
    })
    .transform((c) => ({ ...c, id: c.id ?? c.host }));

// Public-API endpoint patterns: the public API's own keyspace, the path exactly
// as it follows https://api.luminovo.com (`POST /offers/bulk`,
// `GET /sourcing-scenarios/*/cost-breakdown`; a few public paths do begin with
// /api, and those are written as they are). Same exact-route grammar as
// `endpoints`: `*` is the parameter marker, there is no subtree wildcard.
export const PERMISSION_PUBLIC_ENDPOINT_RE = /^(?:GET|POST|PUT|PATCH|DELETE) (?:\/(?:[a-z0-9._-]+|\*))+$/;
export const permissionPublicEndpointGrantSchema = z
    .object({
        patterns: z
            .array(
                z
                    .string()
                    .min(1)
                    .max(120)
                    .regex(
                        PERMISSION_PUBLIC_ENDPOINT_RE,
                        'must be "METHOD /path" as it follows https://api.luminovo.com, naming ONE route with * for id segments (no **)',
                    ),
            )
            .min(1)
            .max(16),
        reason: permissionReason.optional(),
    })
    .strict();

// SRM endpoint patterns: SRM's own keyspace (no /api prefix — e.g.
// `GET /tenant/*/approval/*` for the registry's
// `GET /tenant/:tenant_id/approval/:approval_id`). Exact routes only, like
// `endpoints`: `*` is the parameter marker, there is no subtree wildcard.
export const PERMISSION_SRM_ENDPOINT_RE = /^(?:GET|POST|PUT|PATCH|DELETE) (?:\/(?:[a-z0-9._-]+|\*))+$/;
export const permissionSrmGrantSchema = z
    .object({
        patterns: z
            .array(
                z
                    .string()
                    .min(1)
                    .max(120)
                    .regex(
                        PERMISSION_SRM_ENDPOINT_RE,
                        'must be "METHOD /path" in the SRM keyspace, naming ONE route with * for id segments (no **)',
                    ),
            )
            .min(1)
            .max(16),
        reason: permissionReason.optional(),
    })
    .strict();

// Gateway procedures: EXACT dotted names, validated against the live registry
// at run time (an unknown name is simply inert). No wildcard — the same
// reasoning that removed `**` from endpoint patterns.
export const PERMISSION_GATEWAY_PROCEDURE_RE = /^[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)+$/;
export const permissionGatewayGrantSchema = z
    .object({
        procedures: z
            .array(
                z
                    .string()
                    .min(1)
                    .max(120)
                    .regex(PERMISSION_GATEWAY_PROCEDURE_RE, 'must be an exact dotted procedure name'),
            )
            .min(1)
            .max(32),
        reason: permissionReason.optional(),
    })
    .strict();

// api.reports verbs a workflow may call — the FULL sandbox op set.
export const PERMISSION_REPORT_VERBS = [
    'create',
    'list',
    'get',
    'update',
    'delete',
    'setAccess',
    'setPassword',
    'revokePassword',
    'listPasswords',
    'history',
    'getVersion',
    'restore',
] as const;
export const permissionReportsSchema = z
    .object({
        verbs: z.array(z.enum(PERMISSION_REPORT_VERBS)).min(1).max(12),
        reason: permissionReason.optional(),
    })
    .strict();

// api.files — one switch rather than a verb list: the surface is a single op,
// and a download link addresses only the exec's own session.
export const permissionFilesSchema = z
    .object({
        downloads: z.literal(true),
        reason: permissionReason.optional(),
    })
    .strict();

// Warehouse tables the app queries (snake_case ClickHouse identifiers). The
// catalog is dynamic, so validation is grammar-only and an unknown table is
// inert — the declaration is the strict disclosure enforcement will key on
// once app attribution at the warehouse route is sound.
export const PERMISSION_WAREHOUSE_TABLE_RE = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;
export const permissionWarehouseGrantSchema = z
    .object({
        tables: z
            .array(z.string().min(1).max(80).regex(PERMISSION_WAREHOUSE_TABLE_RE, 'must be a snake_case table name'))
            .min(1)
            .max(32),
        reason: permissionReason.optional(),
    })
    .strict();

// Reach: the Sibling Tenants this app may act in, by explicit lowercase name —
// the shape every production tenant name has, which `*` cannot match, so there
// is no "every tenant that received this app" sentinel to mis-read. Its cap is
// its OWN constant at a deliberately different value: this list rides no HTTP
// header, so the 16KB bound that calibrates PERMISSION_MAX_TOTAL_PATTERNS does
// not apply to it, and sharing one constant would couple two unrelated limits.
export const PERMISSION_TENANT_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
export const PERMISSION_MAX_TENANTS = 16;
export const permissionTenantSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(PERMISSION_TENANT_RE, 'must be a lowercase tenant name of letters, digits and inner hyphens');

export const SchemaPermissionLlm = z
    .object({
        models: z
            .array(SchemaModelClass)
            .min(1, `declare at least one Model Class (${MODEL_CLASSES.join(', ')}), or leave out permissions.llm`),
        reason: permissionReason.optional(),
    })
    .strict();

export const appPermissionsSchema = z
    .object({
        // Every leg defaults to empty so `permissions: {}` is the explicit
        // total-lockdown declaration.
        endpoints: z.array(permissionEndpointGrantSchema).max(32).default([]),
        publicEndpoints: z.array(permissionPublicEndpointGrantSchema).max(32).default([]),
        external: z.array(permissionExternalSchema).max(32).default([]),
        srm: z.array(permissionSrmGrantSchema).max(32).default([]),
        gateway: z.array(permissionGatewayGrantSchema).max(8).default([]),
        reports: permissionReportsSchema.optional(),
        files: permissionFilesSchema.optional(),
        warehouse: z.array(permissionWarehouseGrantSchema).max(8).default([]),
        tenants: z.array(permissionTenantSchema).max(PERMISSION_MAX_TENANTS).default([]),
        llm: SchemaPermissionLlm.optional(),
    })
    .strict()
    // A repeated pattern or host — within one grant or across grants — is a
    // copy-paste error, not a broader grant.
    .superRefine((permissions, ctx) => {
        const totalPatterns = permissions.endpoints.reduce((sum, grant) => sum + grant.patterns.length, 0);
        if (totalPatterns > PERMISSION_MAX_TOTAL_PATTERNS) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endpoints'],
                message: `at most ${PERMISSION_MAX_TOTAL_PATTERNS} endpoint patterns in total (got ${totalPatterns})`,
            });
        }
        const seenPatterns = new Set<string>();
        for (const [grantIndex, grant] of permissions.endpoints.entries()) {
            for (const [patternIndex, pattern] of grant.patterns.entries()) {
                if (seenPatterns.has(pattern)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['endpoints', grantIndex, 'patterns', patternIndex],
                        message: `duplicate endpoint pattern "${pattern}"`,
                    });
                }
                if (PERMISSION_LLM_ROUTE_RE.test(pattern)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['endpoints', grantIndex, 'patterns', patternIndex],
                        message: `"${pattern}" is a model access route: declare permissions.llm instead, which opens this app's own Completion and Decision routes`,
                    });
                }
                seenPatterns.add(pattern);
            }
        }
        const seenIds = new Set<string>();
        for (const [index, entry] of permissions.external.entries()) {
            if (seenIds.has(entry.id)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['external', index, 'id'],
                    message: `duplicate connection id "${entry.id}"`,
                });
            }
            seenIds.add(entry.id);
        }
        // The other legs get the same duplicate rule: a repeated identifier is
        // a copy-paste error, never a broader grant.
        const dedupe = (leg: 'publicEndpoints' | 'srm' | 'gateway' | 'warehouse', field: string, lists: string[][]) => {
            const seen = new Set<string>();
            for (const [grantIndex, values] of lists.entries()) {
                for (const [valueIndex, value] of values.entries()) {
                    if (seen.has(value)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [leg, grantIndex, field, valueIndex],
                            message: `duplicate ${leg} ${field.replace(/s$/, '')} "${value}"`,
                        });
                    }
                    seen.add(value);
                }
            }
        };
        dedupe(
            'publicEndpoints',
            'patterns',
            permissions.publicEndpoints.map((grant) => grant.patterns),
        );
        dedupe(
            'srm',
            'patterns',
            permissions.srm.map((grant) => grant.patterns),
        );
        dedupe(
            'gateway',
            'procedures',
            permissions.gateway.map((grant) => grant.procedures),
        );
        dedupe(
            'warehouse',
            'tables',
            permissions.warehouse.map((grant) => grant.tables),
        );
        const totalPublic = permissions.publicEndpoints.reduce((sum, grant) => sum + grant.patterns.length, 0);
        if (totalPublic > PERMISSION_MAX_TOTAL_PUBLIC_ENDPOINT_PATTERNS) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['publicEndpoints'],
                message: `at most ${PERMISSION_MAX_TOTAL_PUBLIC_ENDPOINT_PATTERNS} publicEndpoints patterns in total (got ${totalPublic})`,
            });
        }
        const totalSrm = permissions.srm.reduce((sum, grant) => sum + grant.patterns.length, 0);
        if (totalSrm > PERMISSION_MAX_TOTAL_SRM_PATTERNS) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['srm'],
                message: `at most ${PERMISSION_MAX_TOTAL_SRM_PATTERNS} srm patterns in total (got ${totalSrm})`,
            });
        }
        const seenTenants = new Set<string>();
        for (const [index, tenant] of permissions.tenants.entries()) {
            if (seenTenants.has(tenant)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['tenants', index],
                    message: `duplicate tenant "${tenant}"`,
                });
            }
            seenTenants.add(tenant);
        }
        // The endpoint lists cover every declared tenant, so reach without one
        // would grant nothing while reading like a grant.
        if (
            permissions.tenants.length > 0 &&
            permissions.endpoints.length === 0 &&
            permissions.publicEndpoints.length === 0
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tenants'],
                message:
                    'the declared endpoints apply unchanged in every declared tenant, so declare at least one endpoints or publicEndpoints grant beside tenants',
            });
        }
        const seenVerbs = new Set<string>();
        for (const [index, verb] of (permissions.reports?.verbs ?? []).entries()) {
            if (seenVerbs.has(verb)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['reports', 'verbs', index],
                    message: `duplicate reports verb "${verb}"`,
                });
            }
            seenVerbs.add(verb);
        }
        const seenModels = new Set<string>();
        for (const [index, model] of (permissions.llm?.models ?? []).entries()) {
            if (seenModels.has(model)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['llm', 'models', index],
                    message: `duplicate Model Class "${model}"`,
                });
            }
            seenModels.add(model);
        }
    });

// ---------------------------------------------------------------------------
// Navigation: what an app places in the product's Navigation Rail while it is
// the active app (lumi-mcp-server spec 2026-09-16). One root Navigation Entry
// with nested children. OPTIONAL — an app that declares none keeps the derived
// convention in packages/frontend/slots (the unique static full-page route that is an
// ancestor of every other one becomes the entry, static routes below it become
// links).
//
// An entry's `path` is a CONCRETE product location, not a route pattern: no
// `:param`, no `*`, but a query string and fragment are allowed, because the
// rail renders it as a link and `/charts?range=30d` is an ordinary destination.
// The platform checks every path against the app's own full-page routes at
// upload (`routeCoversPath` below is the same rule), so an accepted entry is
// always a page the product can mount. Paths are unique within the tree.

// Root plus two levels below it.
export const NAVIGATION_MAX_DEPTH = 3;
export const NAVIGATION_MAX_ENTRIES = 40;

// A lucide icon name, kebab-case (`chart-bar`). This host owns the vocabulary
// and falls back to a neutral glyph for a name it does not know; the platform
// checks only the grammar.
export const NAVIGATION_ICON_NAME_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// An icon is either a lucide name or the same bundle asset `catalog.icon`
// takes.
export const navigationIconSchema = z.union([
    z.string().min(1).max(64).regex(NAVIGATION_ICON_NAME_RE, 'must be a lowercase-dash icon name'),
    catalogIconSchema,
]);

// Absolute path of lowercase-dash segments, then an optional query string and
// fragment. No `:param` or `*` (route-pattern syntax), no whitespace, no
// backslash.
export const NAVIGATION_PATH_RE = /^(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)+(?:\?[^\s#\\]*)?(?:#[^\s\\]*)?$/;

// The label reuses the widget-name shape: a plain string, or a locale map with
// the `en` fallback `resolveLocalizedName` ends on.
export const SchemaNavigationLabel = SchemaWidgetName;

export type NavigationIcon = z.infer<typeof navigationIconSchema>;

// Recursive through a getter — zod v4's idiom for a self-referential shape,
// which also lets the types be inferred rather than stated. Only the getter's
// return type is stated: without it, the declaration can't write the cycle.
export const navigationEntrySchema = z.strictObject({
    label: SchemaNavigationLabel,
    icon: navigationIconSchema.optional(),
    path: z
        .string()
        .min(1)
        .max(200)
        .regex(
            NAVIGATION_PATH_RE,
            'must be an absolute product path of lowercase-dash segments, optionally with a query string and fragment — no :param or * segments',
        ),
    get children(): z.ZodDefault<z.ZodArray<typeof navigationEntrySchema>> {
        return z.array(navigationEntrySchema).default([]);
    },
});

export type NavigationEntry = z.infer<typeof navigationEntrySchema>;
// The shape BEFORE parsing: `children` may be omitted.
export type NavigationEntryInput = z.input<typeof navigationEntrySchema>;

// The pathname of an entry's path — what coverage, ancestry and selection compare.
export function navigationPathname(path: string): string {
    const end = path.search(/[?#]/);
    return end === -1 ? path : path.slice(0, end);
}

const pathSegments = (path: string): string[] => path.split('/').filter((segment) => segment.length > 0);

// Whether a declared full-page widget ROUTE covers a concrete pathname, with the
// product router's semantics: a `:param` segment matches any one segment, and a
// trailing `/*` matches zero or more remaining segments.
export function routeCoversPath(route: string, pathname: string): boolean {
    const routeSegs = pathSegments(route);
    const pathSegs = pathSegments(pathname);
    const wildcard = routeSegs.at(-1) === '*';
    const fixed = wildcard ? routeSegs.slice(0, -1) : routeSegs;
    if (wildcard ? pathSegs.length < fixed.length : pathSegs.length !== fixed.length) return false;
    return fixed.every((segment, index) => segment.startsWith(':') || segment === pathSegs[index]);
}

const isSegmentAncestor = (ancestor: readonly string[], descendant: readonly string[]): boolean =>
    ancestor.length <= descendant.length && ancestor.every((segment, index) => segment === descendant[index]);

// The manifest-level rules for a Navigation, which need the widgets and so
// cannot live on the entry schema: every path is covered by one of the app's
// own full-page routes, every descendant sits below the root, paths are unique,
// and the tree stays within the depth and size the rail is built for.
function validateNavigation(root: NavigationEntry, widgets: readonly Widget[], ctx: z.RefinementCtx): void {
    const routes = widgets.flatMap((widget) => (widget.kind === 'full-page' ? [widget.route] : []));
    const rootSegments = pathSegments(navigationPathname(root.path));
    const seen = new Set<string>();
    let count = 0;
    const visit = (entry: NavigationEntry, path: (string | number)[], depth: number): void => {
        count += 1;
        if (depth > NAVIGATION_MAX_DEPTH) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path,
                message: `navigation may nest at most ${NAVIGATION_MAX_DEPTH - 1} levels below the root`,
            });
            return;
        }
        const pathname = navigationPathname(entry.path);
        if (!routes.some((route) => routeCoversPath(route, pathname))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...path, 'path'],
                message: `"${entry.path}" is not covered by any declared full-page widget route`,
            });
        }
        if (!isSegmentAncestor(rootSegments, pathSegments(pathname))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...path, 'path'],
                message: `"${entry.path}" is not below the root path "${root.path}"`,
            });
        }
        if (seen.has(entry.path)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...path, 'path'],
                message: `duplicate navigation path "${entry.path}"`,
            });
        }
        seen.add(entry.path);
        for (const [index, child] of entry.children.entries()) {
            visit(child, [...path, 'children', index], depth + 1);
        }
    };
    visit(root, ['navigation'], 1);
    if (count > NAVIGATION_MAX_ENTRIES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['navigation'],
            message: `navigation may contain at most ${NAVIGATION_MAX_ENTRIES} entries`,
        });
    }
}

// A named unit of work the app exposes for the host (or another app) to invoke.
// No route and no slot: a workflow is called, not rendered.
export const workflowSchema = z
    .object({
        id: manifestId,
        name: z.string().min(1).max(120),
        paramsSchema: paramsSchemaSchema,
    })
    .strict();

// The product module an app belongs to, used to group apps in the store and
// navigation. A closed enum, unlike the open catalog category/tags vocabulary:
// the module set is the product's fixed top-level areas, so a typo'd value
// fails the upload instead of minting a phantom group. Adding a value is
// backward-compatible; removing one breaks shipped manifests. Versions stored
// before this field existed lack it at runtime despite the required type.
export const APP_MODULES = [
    'design',
    'sourcing',
    'manufacturing',
    'calculation',
    'quotation',
    'risk',
    'suppliers',
    'customers',
    'projects',
    'customer-portal',
    'supplier-portal',
    'platform',
] as const;
export const appModuleSchema = z.enum(APP_MODULES);

export const appManifestSchema = z
    .object({
        manifestVersion: z.literal(MANIFEST_VERSION),
        // Opaque to us — conventionally `<publisher>/<app>` (e.g.
        // `luminovo/app-directory`), but the grammar is the frontend's to own,
        // so we only require a non-empty bounded string.
        id: z.string().min(1).max(128),
        name: z.string().min(1).max(120),
        description: z.string().min(1).max(500),
        module: appModuleSchema,
        // Who to credit, DECLARED because the store's own creator is whichever
        // account uploaded — commonly one shared account for every app. A
        // consumer displays it as the author's claim, never a verified
        // identity, and credits a universal (lumi-*) app to Luminovo instead.
        author: SchemaVisibleWidgetName.optional(),
        // Optional author-owned catalog metadata. Its asset references are
        // metadata only here; upload and file-existence checks belong elsewhere.
        catalog: appCatalogSchema.optional(),
        // REQUIRED: an app states what it reaches, and the outbound worker
        // denies anything the declaration does not cover. `{}` is the legal way
        // to say "nothing", so an app needing no backend still answers the
        // question rather than skipping it.
        permissions: appPermissionsSchema,
        // Both collections default to empty: an app may ship only widgets, only
        // workflows, or (briefly, while being authored) neither.
        widgets: z.array(widgetSchema).default([]),
        workflows: z.array(workflowSchema).default([]),
        // Optional: absent ⇒ the host derives the rail entries from the
        // full-page routes; present ⇒ authoritative.
        navigation: navigationEntrySchema.optional(),
    })
    .strict()
    // Ids are the reference keys a host dispatches on, so a duplicate is
    // ambiguous rather than merely redundant. Widgets and workflows are
    // separate namespaces — the same id in both is fine.
    .superRefine((manifest, ctx) => {
        for (const key of ['widgets', 'workflows'] as const) {
            const seen = new Set<string>();
            for (const [index, entry] of manifest[key].entries()) {
                if (seen.has(entry.id)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [key, index, 'id'],
                        message: `duplicate ${key} id "${entry.id}"`,
                    });
                }
                seen.add(entry.id);
            }
        }
        const workflowIds = new Set(manifest.workflows.map((workflow) => workflow.id));
        for (const [index, widget] of manifest.widgets.entries()) {
            if (widget.kind === 'button') {
                checkButtonTarget(manifest.widgets, widget, index, workflowIds, ctx);
            }
        }
        for (const [index, widget] of manifest.widgets.entries()) {
            for (const [slotIndex, slotId] of widget.slotIds.entries()) {
                const slot = SLOTS[slotId];
                if (slot.kind !== widget.kind) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['widgets', index, 'slotIds', slotIndex],
                        message: `slot "${slotId}" takes a ${slot.kind} widget, not a ${widget.kind}`,
                    });
                    continue;
                }
                const unmet = unmetParamNames(slotId, widget.paramsSchema);
                if (unmet.length > 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['widgets', index, 'paramsSchema', 'required'],
                        message:
                            `slot "${slotId}" does not supply ${unmet.join(', ')} — it supplies ` +
                            `${slotParamNames(slotId).join(', ') || 'nothing'}`,
                    });
                }
            }
        }
        checkTabLabels(manifest.widgets, ctx);
        if (manifest.navigation !== undefined) {
            validateNavigation(manifest.navigation, manifest.widgets, ctx);
        }
    });

// Two tabs of one app in one slot must not share a label. The host tells colliding
// tabs apart by adding the app name, which cannot separate two tabs of the same
// app, and this is the one collision a single upload can see. Labels are compared
// in every locale either tab declares, with English standing in for a locale a
// tab does not declare, as the host falls back to it.
const tabLabels = (name: Widget['name']): Record<string, string> => (typeof name === 'string' ? { en: name } : name);

const tabLabelsCollide = (a: Record<string, string>, b: Record<string, string>): boolean =>
    [...new Set([...Object.keys(a), ...Object.keys(b)])].some((locale) => (a[locale] ?? a.en) === (b[locale] ?? b.en));

function checkTabLabels(widgets: readonly Widget[], ctx: z.RefinementCtx): void {
    const tabs = [...widgets.entries()].filter(([, widget]) => widget.kind === 'tab');
    for (const [i, [index, widget]] of tabs.entries()) {
        for (const [, earlier] of tabs.slice(0, i)) {
            const shared = widget.slotIds.find((slotId) => earlier.slotIds.includes(slotId));
            if (shared === undefined || !tabLabelsCollide(tabLabels(widget.name), tabLabels(earlier.name))) continue;
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['widgets', index, 'name'],
                message: `tab "${widget.id}" has the same label as tab "${earlier.id}" in slot "${shared}"`,
            });
            break;
        }
    }
}

// Every reference a button's action makes has to resolve inside this manifest.
// Refused at upload, because a host that cannot tell what a button does can only
// withhold it, which tells the author nothing.
function checkButtonTarget(
    widgets: readonly Widget[],
    button: ButtonWidget,
    index: number,
    workflowIds: ReadonlySet<string>,
    ctx: z.RefinementCtx,
): void {
    const issue = (path: (string | number)[], message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['widgets', index, ...path], message });
    const { action } = button;
    if (action.type === 'workflow') {
        if (!workflowIds.has(action.workflowId)) {
            issue(
                ['action', 'workflowId'],
                `unknown workflow id "${action.workflowId}" — name a workflow this manifest declares`,
            );
        }
        return;
    }
    // Handlers are not declared in the manifest, so there is nothing to resolve.
    if (action.type === 'call') return;
    const destinationId = action.to.widget;
    const destination = widgets.find((widget) => widget.id === destinationId);
    if (destination === undefined) {
        issue(
            ['action', 'to', 'widget'],
            `unknown widget id "${destinationId}" — navigate to a full-page widget this manifest declares`,
        );
        return;
    }
    if (destination.kind !== 'full-page') {
        issue(
            ['action', 'to', 'widget'],
            `widget "${destinationId}" is a ${destination.kind} widget — navigate to a full-page widget, which has a route`,
        );
        return;
    }
    const mapped = new Set(Object.keys(action.params));
    const routeParams = routeParamNames(destination.route);
    const unmappedRoute = routeParams.filter((name) => !mapped.has(name));
    if (unmappedRoute.length > 0) {
        issue(
            ['action', 'params'],
            `route "${destination.route}" of widget "${destinationId}" needs ${unmappedRoute.join(', ')} — ` +
                'map each to a slot param, e.g. { "slot": "projectId" }',
        );
    }
    const unmappedRequired = (destination.paramsSchema.required ?? []).filter((name) => !mapped.has(name));
    if (unmappedRequired.length > 0) {
        issue(
            ['action', 'params'],
            `widget "${destinationId}" requires ${unmappedRequired.join(', ')} — map each to a slot param`,
        );
    }
    const accepted = new Set([...routeParams, ...Object.keys(destination.paramsSchema.properties)]);
    for (const [name, source] of Object.entries(action.params)) {
        if (!accepted.has(name)) {
            issue(
                ['action', 'params', name],
                `widget "${destinationId}" takes no param "${name}" — map only its route params ` +
                    `(${routeParams.join(', ') || 'none'}) and paramsSchema properties`,
            );
        }
        for (const slotId of button.slotIds) {
            // A slot of the wrong kind is already refused below; say nothing twice.
            if ((SLOTS[slotId] as SlotDefinition).kind !== 'button') continue;
            const type = slotParamType(slotId, source.slot);
            if (type === undefined) {
                issue(
                    ['action', 'params', name, 'slot'],
                    `slot "${slotId}" does not supply "${source.slot}" — it supplies ` +
                        `${slotParamNames(slotId).join(', ') || 'nothing'}`,
                );
            } else if (type !== 'string') {
                issue(
                    ['action', 'params', name, 'slot'],
                    `slot param "${source.slot}" of "${slotId}" is ${type === 'object' ? 'an' : 'a'} ${type} — only string params can go into a URL`,
                );
            }
        }
    }
}

export type ParamsSchema = z.infer<typeof paramsSchemaSchema>;
export type FullPageWidget = z.infer<typeof fullPageWidgetSchema>;
export type ButtonWidget = z.infer<typeof buttonWidgetSchema>;
export type ContainerWidget = z.infer<typeof containerWidgetSchema>;
export type TabWidget = z.infer<typeof tabWidgetSchema>;
export type ButtonAction = z.infer<typeof buttonActionSchema>;
export type Widget = z.infer<typeof widgetSchema>;
export type WidgetKind = Widget['kind'];
export type AppPermissions = z.infer<typeof appPermissionsSchema>;
export type PermissionEndpointGrant = z.infer<typeof permissionEndpointGrantSchema>;
export type PermissionExternal = z.infer<typeof permissionExternalSchema>;
export type PermissionSrmGrant = z.infer<typeof permissionSrmGrantSchema>;
export type PermissionGatewayGrant = z.infer<typeof permissionGatewayGrantSchema>;
export type PermissionReports = z.infer<typeof permissionReportsSchema>;
export type PermissionWarehouseGrant = z.infer<typeof permissionWarehouseGrantSchema>;
export type PermissionTenants = AppPermissions['tenants'];
export type PermissionLlm = z.infer<typeof SchemaPermissionLlm>;
export type CatalogCategory = z.infer<typeof catalogCategorySchema>;
export type CatalogIcon = z.infer<typeof catalogIconSchema>;
export type CatalogCoverImage = z.infer<typeof catalogCoverImageSchema>;
export type AppCatalog = z.infer<typeof appCatalogSchema>;
export type Workflow = z.infer<typeof workflowSchema>;
export type AppModule = z.infer<typeof appModuleSchema>;
export type AppManifest = z.infer<typeof appManifestSchema>;

export class ManifestValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ManifestValidationError';
    }
}

// The one entry point for turning untrusted JSON into an AppManifest. Throws
// ManifestValidationError with every issue flattened into one message, so a
// caller can hand the text straight back to whoever authored the manifest.
export function parseAppManifest(input: unknown): AppManifest {
    const result = appManifestSchema.safeParse(input);
    if (result.success) return result.data;
    const issues = result.error.issues
        .map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`)
        .join('; ');
    throw new ManifestValidationError(`invalid app manifest — ${issues}`);
}
